// Quality › Tests and Suggestions (chatbot_v2_mono spec 2026-10-12-tester-suggestions-design.md §4.4).
// No imports on purpose: `node --test` loads this file directly.

export const QUALITY_TABS = ['review', 'tests', 'suggestions', 'latin'] as const;
export type QualityTab = (typeof QUALITY_TABS)[number];

/** The Quality page's tab from `?tab=`; anything unknown opens Answers to review. v1 links to `tests` / `suggestions`. */
export function qualityTab(param: string | null | undefined): QualityTab {
  return (QUALITY_TABS as readonly string[]).includes(String(param)) ? (param as QualityTab) : 'review';
}

export interface TesterBot {
  system: string;
  botId: string;
}

export function testerPath(bot: TesterBot, rest: string): string {
  return `/api/tester/${encodeURIComponent(bot.system)}/${encodeURIComponent(bot.botId)}/${rest}`;
}

/** A run's own page inside Quality › Tests. */
export function runLink(bot: TesterBot, jobId: string): string {
  return `/bots/${bot.system}/${encodeURIComponent(bot.botId)}/quality?tab=tests&run=${encodeURIComponent(jobId)}`;
}

// ── the hub's /api/tester replies ───────────────────────────────────────────

export interface TesterQuestion {
  id: number;
  question: string;
  status: 'active' | 'stale';
  origin: string;
  documentId: number | null;
  chunkId: number | null;
  /** null = not set, 'none' = no block is right, else a block link's ref. */
  expectedBlock: string | null;
  createdAt: string;
}
export interface QuestionPage {
  items: TesterQuestion[];
  total: number;
}
export interface Freshness {
  freshness: 'current' | 'stale' | 'missing' | 'no_index';
  lastIndexAt: string | null;
  lastGenqAt: string | null;
}
export interface Estimate {
  questions: number;
  available: number;
  capped: boolean;
  maxQuestions: number;
  costUsd: number | null;
  seconds: number | null;
  worstSeconds: number;
  lastRun: { jobId: string; finishedAt: string | null } | null;
  flowVersion: string;
  channel: string;
}
export interface RunSummary {
  total: number;
  answered: number;
  ok: number;
  fail: number;
  error: number;
  pending: number;
  score: number | null;
  botInputTokens: number;
  botOutputTokens: number;
  available: number;
  capped: boolean;
  startedAt?: string;
  finishedAt?: string;
  runSeconds?: number;
  runCostUsd?: number;
  /** Questions with an expected block that got a verdict, how many got the right block, and the share (null = none judged). */
  blockJudged?: number;
  blockRight?: number;
  blockRate?: number | null;
}
export interface RunModel {
  model: string;
  llmCalls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}
export interface Run {
  jobId: string;
  status: string;
  error: string | null;
  requestedBy: string | null;
  channel: string | null;
  createdAt: string;
  updatedAt: string;
  cancelRequested: boolean;
  summary: RunSummary | null;
  usage: { costUsd: number; llmCalls: number } | null;
  models: RunModel[];
}
export interface DocRef {
  id: string;
  title: string | null;
  origin: string | null;
  exists: boolean;
}
export type Verdict = 'ok' | 'fail' | 'error' | 'pending';
export interface RunItem {
  questionId: string;
  question: string;
  answer: string | null;
  verdict: Verdict;
  reason: string | null;
  documentIds: string[];
  documents: DocRef[];
  responseMs: number | null;
  answerModel: string | null;
  createdAt: string;
  expectedBlock?: string | null;
  blockMode?: 'block_only' | 'attach' | 'none' | null;
  blockRef?: string | null;
  blockScore?: number | null;
  /** llm-tester's full trace of the block decision (candidates, thresholds); null when none was made. */
  block?: Record<string, unknown> | null;
  /** llm-tester's judgement: null = not judged (no expectation, or no decision). */
  blockRight?: boolean | null;
}
export type SuggestionStatus = 'pending' | 'accepted' | 'rejected' | 'reverted' | 'superseded' | 'expired';
export interface Suggestion {
  id: string;
  documentId: string;
  evalJobId: string | null;
  failedQuestion: string | null;
  oldContent: string;
  proposedContent: string;
  status: SuggestionStatus;
  createdAt: string | null;
  decidedAt: string | null;
  decidedBy: string | null;
  document: DocRef;
  /** `replace` = Accept replaces the document's text; `faq` = a website-page chunk: Accept adds an FAQ document instead. */
  acceptPath: 'replace' | 'faq';
  /** The FAQ document an accepted `faq` suggestion created (null otherwise). */
  faqDocument: DocRef | null;
  /** Pending, and the document no longer reads the text the suggestion was made from: accept would be refused. */
  stale: boolean;
  /** Accepted in the hub and the document still reads the accepted text. */
  undoable: boolean;
}
export interface AcceptResult {
  id: string;
  ok: boolean;
  status: 'accepted' | 'already_accepted' | 'changed' | 'not_found' | 'not_pending' | 'failed';
  path?: 'replace' | 'faq';
  documentId?: string;
  faqDocumentId?: string;
  revisionId?: string;
  reindexed?: boolean;
  marked?: boolean;
  message?: string;
}
export interface UndoResult {
  id: string;
  path: 'replace' | 'faq';
  documentId: string;
  faqDocumentId?: string;
  status: 'reverted';
  revisionId?: string;
  reindexed?: boolean;
  marked: boolean;
  message?: string;
}
export type SuggestOutcome =
  | { state: 'done'; jobId: string | null; suggestions: unknown[]; persisted: number; failedItems: number; documents: number; failedBatches: number }
  | { state: 'working'; jobId: string };
export interface ProgressEvent {
  type: string;
  data?: any;
}

// ── tests ───────────────────────────────────────────────────────────────────

export function freshnessNotice(f: Freshness | null | undefined): { tone: 'warn' | 'info'; text: string } | null {
  switch (f?.freshness) {
    case 'stale':
      return { tone: 'warn', text: 'The knowledge was rebuilt after these questions were made — some point at chunks that no longer exist. Make questions again.' };
    case 'missing':
      return { tone: 'info', text: 'No questions yet — make them from the bot’s documents.' };
    case 'no_index':
      return { tone: 'info', text: 'This bot has no built knowledge yet — build it in Knowledge › Builder first.' };
    default:
      return null;
  }
}

export function fmtDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—';
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s} s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min`;
  return `${Math.floor(m / 60)} h ${m % 60} min`;
}

export function fmtUsd(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `$${v.toFixed(v < 1 ? 4 : 2)}`;
}

/** What the confirm box says before a run starts (spec §4.1 size cap, §4.4). */
export function estimateLines(e: Estimate): string[] {
  return [
    `${e.questions} question${e.questions === 1 ? '' : 's'}${e.capped ? ` (the first ${e.questions} of ${e.available})` : ''}`,
    e.costUsd == null ? 'Cost: unknown until the first run finishes' : `Cost: about ${fmtUsd(e.costUsd)} (Claude scoring — internal, never billed to the customer)`,
    e.seconds == null ? `Time: at most about ${fmtDuration(e.worstSeconds)}` : `Time: about ${fmtDuration(e.seconds)}`,
  ];
}

/** Spec §4.2 D8: flow-v2 bots are still tested on the v1 answer path. */
export function flowV2Notice(flowVersion: string | null | undefined): string | null {
  return flowVersion === 'v2' ? 'This bot answers with flow v2; the test uses the v1 path.' : null;
}

const ACTIVE = ['pending', 'running', 'paused'];
export const isActiveRun = (status: string | null | undefined): boolean => ACTIVE.includes(String(status));

export function scoreText(s: Pick<RunSummary, 'score'> | null | undefined): string {
  return s?.score == null ? '—' : `${(s.score * 100).toFixed(1)}%`;
}

/** Quality › Tests "Right block" (knowledge→block plan 4). Both read llm-tester's numbers; nothing is recomputed here. */
export function rateLabel(s: Pick<RunSummary, 'blockJudged' | 'blockRight' | 'blockRate'> | null | undefined): string {
  if (!s || !s.blockJudged || s.blockRate == null) return '—';
  return `${s.blockRight ?? 0}/${s.blockJudged} · ${Math.round(s.blockRate * 100)}%`;
}

/** ✓ the run chose the block the question names (or none when none is right), ✕ it did not, null = not judged. */
export function blockVerdict(item: Pick<RunItem, 'blockRight'>): '✓' | '✕' | null {
  return item.blockRight === true ? '✓' : item.blockRight === false ? '✕' : null;
}

/** A ref as the person knows it: the link's title, "No block" for 'none', the raw ref if the link is gone. */
export function blockName(ref: string | null | undefined, titles: Record<string, string> = {}): string {
  if (ref == null || ref === '') return '—';
  return ref === 'none' ? 'No block' : titles[ref] || ref;
}

/** "Expected: X · chosen: Y (attach, 0.81)" for an item that has an expectation; null when it has none. */
export function blockLine(item: RunItem, titles: Record<string, string> = {}): string | null {
  if (item.expectedBlock == null) return null;
  const chosen = item.blockMode == null ? 'no decision' : item.blockMode === 'none' || !item.blockRef ? 'No block' : blockName(item.blockRef, titles);
  const detail = [item.blockMode, item.blockScore == null ? null : item.blockScore.toFixed(2)].filter(Boolean).join(', ');
  return `Expected: ${blockName(item.expectedBlock, titles)} · chosen: ${chosen}${detail ? ` (${detail})` : ''}`;
}

export interface LinkOption {
  blockRef: string;
  title: string;
  channel: string | null;
  enabled: boolean;
}
/** The Tester asks v2 bots on Instagram, so a v2 question can only expect an Instagram block. */
export const V2_TESTER_CHANNEL = 'INSTAGRAM';

/** The picker's choices: the bot's enabled links (v2: only the Tester's channel), plus the question's current ref if it is no longer offered. */
export function expectedOptions(links: LinkOption[], system: string, current?: string | null): { ref: string; title: string }[] {
  const out = links
    .filter((l) => l.enabled && (system !== 'chatbot_v2' || (l.channel ?? '').toUpperCase() === V2_TESTER_CHANNEL))
    .map((l) => ({ ref: l.blockRef, title: l.title }));
  if (current && current !== 'none' && !out.some((o) => o.ref === current)) {
    const was = links.find((l) => l.blockRef === current);
    out.push({ ref: current, title: `${was?.title ?? current} (not available)` });
  }
  return out;
}

/** Wrong block first; the rest keep their order. */
export function wrongBlocksFirst<T extends Pick<RunItem, 'blockRight'>>(items: T[]): T[] {
  return [...items.filter((i) => i.blockRight === false), ...items.filter((i) => i.blockRight !== false)];
}

/** The tested bot's own answer tokens (`bot:<model>`, cost 0 — internal, never billed). */
export function botTokens(models: RunModel[] | null | undefined): { model: string; input: number; output: number }[] {
  return (models ?? [])
    .filter((m) => String(m.model).startsWith('bot:'))
    .map((m) => ({ model: m.model.slice(4), input: m.inputTokens, output: m.outputTokens }));
}

/**
 * RunView keeps the answer list current while a run is active: reload when progress (`key`) moved since the last
 * reload, at most every `gapMs`. Returns the delay before reloading, or null when nothing moved.
 */
export function itemsRefreshWait(key: string, last: { key: string; at: number }, now: number, gapMs = 4_000): number | null {
  if (key === last.key) return null;
  return Math.max(0, gapMs - (now - last.at));
}

/** Live progress of a run from its events: `question` once per asked question, `scored` per batch (run-wide counts). */
export function runProgress(
  events: ProgressEvent[],
  total: number,
): { asked: number; errors: number; total: number; scored: { ok: number; fail: number; error: number; pending: number; score: number | null } | null } {
  const seen = new Set<string>();
  let asked = 0;
  let errors = 0;
  let scored: { ok: number; fail: number; error: number; pending: number; score: number | null } | null = null;
  for (const e of events) {
    if (e.type === 'question' && e.data) {
      const id = String(e.data.questionId ?? '');
      if (seen.has(id)) continue;
      seen.add(id);
      asked += 1;
      if (e.data.status === 'error') errors += 1;
    } else if (e.type === 'scored' && e.data) {
      scored = {
        ok: Number(e.data.ok) || 0,
        fail: Number(e.data.fail) || 0,
        error: Number(e.data.error) || 0,
        pending: Number(e.data.pending) || 0,
        score: e.data.score == null ? null : Number(e.data.score),
      };
    }
  }
  return { asked, errors, total, scored };
}

export const VERDICT_LABEL: Record<Verdict, string> = { ok: 'Right', fail: 'Wrong', error: 'No answer', pending: 'Not scored' };
export const VERDICT_TONE: Record<Verdict, string> = {
  ok: 'bg-green-50 text-green-700',
  fail: 'bg-red-50 text-red-700',
  error: 'bg-amber-50 text-amber-800',
  pending: 'bg-gray-100 text-gray-600',
};

/** hubClient errors read "<status> <text> — <json body>": the run a 409 run_active names, else null. */
export function activeRunId(err: unknown): string | null {
  const msg = err instanceof Error ? err.message : String(err);
  const i = msg.indexOf(' — ');
  if (i < 0) return null;
  try {
    const body = JSON.parse(msg.slice(i + 3)) as { code?: unknown; detail?: { activeJobId?: unknown } };
    return body?.code === 'run_active' && typeof body.detail?.activeJobId === 'string' ? body.detail.activeJobId : null;
  } catch {
    return null;
  }
}

export function suggestOutcomeText(o: SuggestOutcome): string {
  if (o.state === 'working') return 'Still making suggestions — they appear in the Suggestions tab as they are ready.';
  if (!o.failedItems) return 'No wrong answers to fix in this run.';
  const failed = o.failedBatches
    ? ` (${o.failedBatches} batch${o.failedBatches === 1 ? '' : 'es'} failed — press Suggest fixes again for the rest)`
    : '';
  return `${o.persisted} suggestion${o.persisted === 1 ? '' : 's'} made for ${o.documents} document${o.documents === 1 ? '' : 's'}${failed}.`;
}

// ── suggestions ─────────────────────────────────────────────────────────────

/** Accepts per call from the page: every accept re-chunks and re-embeds a document, and the hub's proxy waits 60 s. */
export const ACCEPT_BATCH = 5;

export function chunks<T>(xs: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < xs.length; i += size) out.push(xs.slice(i, i + size));
  return out;
}

/** Re-saving unchanged text re-indexes nothing; Sync re-chunks and re-embeds the document. */
const REINDEX_FAILED = 'Re-indexing failed — press Sync on the document in Knowledge › Documents, or the bot cannot find it.';

/** Rejects per call: the server takes at most 20 ids at once (no document is written, so no smaller batch is needed). */
export const REJECT_BATCH = 20;

export function acceptMessage(r: AcceptResult): string {
  switch (r.status) {
    case 'accepted':
      if (r.marked === false) {
        const m = r.message ?? 'Saved, but the decision was not recorded — accept again to record it.';
        return r.reindexed === false ? `${m} ${REINDEX_FAILED}` : m;
      }
      if (r.reindexed === false) return `Saved. ${REINDEX_FAILED}`;
      return r.path === 'faq' ? 'Added an FAQ document — the website page is unchanged.' : 'Accepted — the document is updated.';
    case 'already_accepted':
      return 'Already accepted.';
    case 'changed':
      return 'The document changed since this suggestion was made — make suggestions again.';
    default:
      return r.message ?? (r.status === 'not_found' ? 'Not found for this bot.' : r.status === 'not_pending' ? 'No longer pending.' : 'Could not accept — try again.');
  }
}

export function undoMessage(r: UndoResult): string {
  if (r.marked === false) {
    const m = 'Restored, but the decision was not recorded — press Undo again to record it.';
    return r.reindexed === false ? `${m} ${REINDEX_FAILED}` : m;
  }
  if (r.reindexed === false) return `Restored. ${REINDEX_FAILED}`;
  return r.path === 'faq' ? 'Undone — the FAQ document was deleted.' : 'Undone — the previous text is back.';
}

/**
 * What Accept will do (pending) or did (accepted) — spec §3.8: a website-page (builder) chunk is never edited, because
 * the next rebuild would erase the change; an FAQ document is added instead.
 */
export function acceptPathText(s: Pick<Suggestion, 'acceptPath' | 'status' | 'document'>): string {
  const doc = s.document.title || `document ${s.document.id}`;
  if (s.acceptPath === 'faq') {
    return s.status === 'pending'
      ? 'Adds an FAQ document (this text came from a website page; a rebuild would erase an in-place change)'
      : 'Added as an FAQ document — the website page’s text was left as it is';
  }
  return s.status === 'pending' ? `Replaces the text of ${doc}` : `Replaced the text of ${doc}`;
}

/** The confirm before Undo — on the FAQ path Undo deletes the FAQ document; it does not touch the website-page chunk. */
export function undoConfirmText(s: Pick<Suggestion, 'acceptPath'>): string {
  return s.acceptPath === 'faq'
    ? 'Delete the FAQ document this suggestion added? The website-page text was never changed.'
    : 'Put the document’s previous text back?';
}
